import { assertPresence } from "@/v2-common/utils/utils.assertion"

export function downloadBlob(data: string, fullFileName: string) {
  const link = document.createElement("a")
  link.setAttribute("href", data)
  link.setAttribute("download", fullFileName)
  link.style.visibility = "hidden"
  const mount = document.querySelector("#mount")
  assertPresence(mount)
  mount.append(link)
  link.click()
  link.remove()
}

export function toBlobUrl(data: string[], options?: BlobPropertyBag) {
  const blob = new Blob(data, options)
  return URL.createObjectURL(blob)
}

export const downloadBase64AsExcel = (base64Data: string, filename: string) => {
  const byteCharacters = atob(base64Data)
  const byteNumbers = new Uint8Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i++) {
    const byteCodePoint = byteCharacters.codePointAt(i)
    assertPresence(byteCodePoint)
    byteNumbers[i] = byteCodePoint
  }

  const blob = new Blob([byteNumbers], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  const url = URL.createObjectURL(blob)

  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()

  link.remove()
  URL.revokeObjectURL(url)
}

type SaveFilePickerAcceptType = {
  description?: string
  accept: Record<string, string[]>
}

type SaveFilePickerOptions = {
  suggestedName?: string
  types?: SaveFilePickerAcceptType[]
}

type SaveFilePickerWindow = Window & {
  showSaveFilePicker: (options?: SaveFilePickerOptions) => Promise<{
    createWritable: () => Promise<{
      write: (data: Blob) => Promise<void>
      close: () => Promise<void>
    }>
  }>
}

function isSaveFilePickerSupported(win: Window): win is SaveFilePickerWindow {
  return "showSaveFilePicker" in win
}

/**
 * Save a blob via the native "Save as…" dialog (File System Access API),
 * letting the user pick the destination. Falls back to a regular anchor
 * download (default downloads folder) in browsers without support
 * (Firefox, Safari). Returns false when the user cancels the picker.
 */
export async function saveBlobAs(
  blob: Blob,
  suggestedName: string,
  types?: SaveFilePickerAcceptType[],
) {
  if (!isSaveFilePickerSupported(window)) {
    const url = URL.createObjectURL(blob)
    downloadFile(url, suggestedName, "#mount")
    URL.revokeObjectURL(url)
    return true
  }
  try {
    const handle = await window.showSaveFilePicker({ suggestedName, types })
    const writable = await handle.createWritable()
    await writable.write(blob)
    await writable.close()
    return true
  } catch (error) {
    // the user closing the picker is not an error
    if (error instanceof DOMException && error.name === "AbortError") {
      return false
    }
    throw error
  }
}

export function downloadFile(
  url: string,
  name: string,
  elementSelector: string,
) {
  const link = document.createElement("a")
  link.href = url
  link.download = name

  // a missing mount must not silently swallow the download — fall back
  // to <body> so the click always fires
  const mount = document.querySelector(elementSelector) ?? document.body
  mount.append(link)
  link.click()
  link.remove()
}
